import { useInfiniteQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export interface CraftingLogSummary {
  logId: string;
  userId: string;
  recipeId: string | null;
  recipeType: string;
  recipeName: string;
  inputsConsumed: Array<{ itemName: string; quantity: number }>;
  outputsCreated: Array<{ itemName: string; quantity: number }>;
  metadata: Record<string, unknown>;
  createdAt: string;
}

interface UseCraftingLogOptions {
  userId?: string;
  type?: string;
  admin?: boolean;
}

interface UseCraftingLogReturn {
  entries: CraftingLogSummary[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  loadMore: () => void;
  hasMore: boolean;
}

const LIMIT = 50;

export function useCraftingLog(options: UseCraftingLogOptions = {}): UseCraftingLogReturn {
  const filters: Record<string, unknown> = {};
  if (options.userId) filters.userId = options.userId;
  if (options.type) filters.type = options.type;
  if (options.admin) filters.admin = options.admin;

  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: queryKeys.crafting.log(filters),
      queryFn: async ({ pageParam = 0 }) => {
        const params: Record<string, string> = {};
        if (options.userId) params.userId = options.userId;
        if (options.type) params.type = options.type;
        params.limit = String(LIMIT);
        params.offset = String(pageParam);

        const path = options.admin ? '/arches/crafting/admin/log' : '/arches/crafting/log';
        const { data } = await api.get<{ entries: CraftingLogSummary[]; total: number }>(path, {
          params,
        });
        return data;
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const next = allPages.length * LIMIT;
        return next < lastPage.total ? next : undefined;
      },
    });

  const entries = data?.pages.flatMap((p) => p.entries) ?? [];
  const total = data?.pages[0]?.total ?? 0;

  return {
    entries,
    total,
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load crafting log' : null,
    refresh: () => {
      refetch();
    },
    loadMore: () => {
      if (hasNextPage && !isFetchingNextPage) fetchNextPage();
    },
    hasMore: !!hasNextPage,
  };
}
